import { makeAutoObservable } from 'mobx'
import { toastStore } from 'shared/ui'
import { type IEntitiesStore } from 'entities/EntitiesExport'
import { type IResponseInbox } from 'entities/Inbox/api/types'
import { type IUser } from 'entities/Users'
import { Phone } from 'entities/Phone/model/Phone'
import { ComplianceApi } from 'entities/Compliance'
import {
  type IAddNumberType,
  NewInboxStepsCommonStore,
} from 'pages/settings/store/newInboxSteps/newInboxCommonStepsStore'
import { type SettingsStore } from 'pages/settings/store/settingsStore'
import type { IAssignFlow } from './flows'

type StepKey =
  | 'newInbox'
  | 'howAddNumber'
  | 'addSalesmsgNumbers'
  | 'payment'
  | 'chooseNumber'
  | 'selectAppAndNumbers'

const stepTitles: Record<StepKey, string> = {
  newInbox: 'Create inbox',
  howAddNumber: 'How do you want to add a number?',
  addSalesmsgNumbers: 'Add Salesmsg numbers',
  payment: 'Payment',
  chooseNumber: 'Choose number',
  selectAppAndNumbers: 'Select app & numbers',
}

export class AssignNewInboxStore {
  private _currentStep = 1
  private _addNumberType: IAddNumberType = 'default'
  private _shouldShowPaymentStep = false
  private _flow: IAssignFlow | null = null
  readonly commonStore: NewInboxStepsCommonStore

  constructor(readonly entities: IEntitiesStore, readonly settings: SettingsStore) {
    this.commonStore = new NewInboxStepsCommonStore(entities, {
      nextClickHandler: () => {
        this._shouldShowPaymentStep = true
        this.nextStep()
      },
      onPaymentNotRequired: () => {
        this.createInbox()
      },
      getAddNumberType: () => this._addNumberType,
      getSelectedNumbersCount: () => this.selectedNumbersCount,
    })

    makeAutoObservable(this)
  }

  init = async () => {
    await this.commonStore.init()
  }

  get stepMap(): Record<number, StepKey> {
    const defaultStepMap: Record<number, StepKey> = {
      1: 'newInbox',
      2: 'howAddNumber',
      3: 'addSalesmsgNumbers',
    }

    switch (this._addNumberType) {
      case 'assign': {
        const stepMap = { ...defaultStepMap }
        if (this._shouldShowPaymentStep) {
          stepMap[4] = 'payment'
        }
        return stepMap
      }
      case 'buy': {
        return {
          ...defaultStepMap,
          3: 'chooseNumber',
          4: 'payment',
        }
      }
      case 'app': {
        return {
          ...defaultStepMap,
          3: 'selectAppAndNumbers',
          4: 'payment',
        }
      }
      default:
        return defaultStepMap
    }
  }

  get newInboxStore() {
    return this.commonStore.newInboxStore
  }

  get isLastStep() {
    return this._currentStep === this.allStepsLength
  }

  get isFirstStep() {
    return this._currentStep === 1
  }

  get nextDisabled() {
    return this.commonStore.newInboxStore.nextDisabled
  }

  get stepLoading() {
    return this.commonStore.stepLoading
  }

  get allStepsLength() {
    return Object.keys(this.stepMap).length
  }

  get currentStep() {
    return this.getStep(0)
  }

  get getNextStep() {
    const step = this.getStep(1)
    if (!step) return this.currentStep
    return step
  }

  get getLastStep() {
    const step = this.getStep(-1)
    if (!step) return this.currentStep
    return step
  }

  getStep = (direction: -1 | 1 | 0): StepKey | undefined => {
    const allSteps = Object.values(this.stepMap)
    const currentPageIndex = this._currentStep - 1
    const targetIndex = currentPageIndex + direction

    if (targetIndex < 0 || targetIndex >= allSteps.length) {
      return undefined
    }

    return allSteps[targetIndex]
  }

  getStepByStepKey = (stepKey: StepKey): number => {
    const allPaths = Object.entries(this.stepMap)
    const stepIndex = allPaths.findIndex((path) => {
      return path[1] === stepKey
    })

    return stepIndex !== -1 ? stepIndex + 1 : 1
  }

  get settingsStep() {
    return this._currentStep
  }

  get progressTitle() {
    if (!this.currentStep) return ''
    return stepTitles[this.currentStep] || ''
  }

  get selectedNumbersCount() {
    if (this.commonStore.isBuyingNumber) {
      return this.commonStore.chooseNumberStore.chooseNumberTabsStore.selectedNumber ? 1 : 0
    }
    if (this._addNumberType === 'app') {
      return this.commonStore.selectAppAndNumbersStore.selectedNumber ? 1 : 0
    }
    return this.commonStore.addSalesmsgNumbersStore.numbersStore.multiSelectedValues.length
  }

  setCurrentStep = (step: number) => {
    this._currentStep = step
  }

  setAddNumberType = (type: IAddNumberType) => {
    this._addNumberType = type
    this._shouldShowPaymentStep = false
  }

  handleAddNumbersNextStep = (handleNextClick: () => void) => {
    if (this.commonStore.isPaymentStepEnabled) {
      this._shouldShowPaymentStep = true
      this.nextStep()
    } else {
      handleNextClick()
    }
  }

  nextStep = () => {
    const nextStepKey = this.getNextStep
    if (!nextStepKey) return

    const nextStepNumber = this.getStepByStepKey(nextStepKey)
    this.setCurrentStep(nextStepNumber)
  }

  decreaseStep = () => {
    if (this.currentStep === 'payment') {
      this._shouldShowPaymentStep = false
    }

    if (this.getLastStep === 'howAddNumber') {
      this.resetStores()
    }

    const lastStepKey = this.getLastStep
    if (!lastStepKey) return

    const lastStepNumber = this.getStepByStepKey(lastStepKey)
    this.setCurrentStep(lastStepNumber)
  }

  get addNumberType() {
    return this._addNumberType
  }

  get shouldIncludeMember() {
    return this._flow?.shouldIncludeMemberInNewInbox() ?? true
  }

  setFlow = (flow: IAssignFlow) => {
    this._flow = flow
  }

  setSelectedToAssignInboxMember = (member: IUser) => {
    if (this.entities.usersStore.membersCount > 1) {
      this.commonStore.newInboxStore.setSelectedMembers({
        id: member.id,
        label: member.name,
        value: member.name,
        data: member,
        avatarProps: {
          online: !member.isAway,
          size: 16,
          info: member.avatarInfo,
        },
      })
    }
  }

  private handleInboxCreationSuccess = async (inbox: IResponseInbox): Promise<void> => {
    this.entities.inboxesStore.addItems([inbox])

    if (this._flow) {
      const result = await this._flow.handleNewInboxCreated(inbox.id)
      if (result.success) {
        toastStore.add({
          type: 'success',
          title: this._flow.getNewInboxSuccessToast(inbox.name),
        })
      }
    } else {
      toastStore.add({
        type: 'success',
        title: `Assigned to ${inbox.name} inbox`,
      })
    }
  }

  successAssignInbox = async (inbox: IResponseInbox) => {
    await this.handleInboxCreationSuccess(inbox)
  }

  successBuyNumberAndCreateInbox = async (inbox: IResponseInbox) => {
    await this.handleInboxCreationSuccess(inbox)

    const { selectedUseCaseId, useCases, completedUseCases, useCaseNumberIds } =
      this.commonStore.chooseNumberStore
    const phone = inbox.number
    const addUseCaseLater = selectedUseCaseId === -1
    const noUseCasesYet = useCases.length === 0
    const noCompletedUseCases = completedUseCases.length === 0
    if (!phone) return

    const needVerify =
      phone.is_toll_free && (addUseCaseLater || noUseCasesYet || noCompletedUseCases)
    if (needVerify) {
      this.entities.complianceStore.setUnverifiedTollFreeNumber(new Phone(phone, this.entities))
    }

    if (selectedUseCaseId !== -1 && selectedUseCaseId) {
      await ComplianceApi.submitTollFreeForReview(selectedUseCaseId, {
        numbers: useCaseNumberIds ? [...useCaseNumberIds, phone.id] : [phone.id],
        additional_numbers: [phone.id],
      })
    }

    if (needVerify && !this.entities.organizationStore.isTwilioNumberVendor) {
      setTimeout(() => {
        this.settings.openVerifyNumberModal(phone, inbox.name || '')
      }, 500)
    }
  }

  createInbox = async () => {
    this.commonStore.paymentStore.setPaymentError(false)
    const params = {
      name: this.commonStore.newInboxStore.newInboxName,
      owner: { id: Number(this.commonStore.newInboxStore.activeInboxOwner) },
      members: this.shouldIncludeMember ? this.commonStore.newInboxStore.selectedMembersIds : [],
      number_vendor_id: this.entities.complianceStore.numberVendorId,
    }

    switch (this._addNumberType) {
      case 'assign':
        return await this.commonStore.assignToNewInbox(params, {
          onSuccess: this.successAssignInbox,
        })
      case 'buy':
        return await this.commonStore.buyNumberAndCreateInbox(params, {
          onSuccess: this.successBuyNumberAndCreateInbox,
        })
      case 'app':
        return await this.commonStore.createInboxWithAppNumber(params, {
          onSuccess: this.successAssignInbox,
        })
      default:
        return null
    }
  }

  reset = () => {
    this._currentStep = 1
    this._addNumberType = 'default'
    this.commonStore.newInboxStore.reset()
    this.resetStores()
  }

  resetStores = () => {
    this.commonStore.resetStores()
  }
}
